import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { HISTORY,FWDS,SURFACE,AS_OF,TENORS,EXPIRIES,EXPIRY_Y,EXPIRY_DAYS,nearestExpiry,STRIKES_BP,C,F,MONO,SANS,normCdf,normPdf,bachelier,bachelierDelta,bachelierVega,buildCubicSpline,buildMonotoneCubic,_splineCache,volAtStrike,volCubeInterp,buildCustomCube,dv01For,atmFwd,marketConeStrikes,priceLeg,payoffAtExpiry,netMetrics,payoffProfile,breakevens,strategyStats,rnDensity,probProfit,expectedPnl,_cubeCache,integrateDensity,readCube,snapToGrid,normalizeView,generateCandidates,priceLegs,computeSizing,buildUserPdf,integrateDist,evalUnderDist,moontowerCheck,isDegenerate,runPipeline,genStrategiesForTenor,displayStrike,WINDOW_DAYS,addDays,interpConeStrikes,RateChart,PayoffChart,SmilePanel,ExclusionRow,CURVE_STRATEGIES, } from './ratesShared';
import { S, btn, badge, logoIcon, spotNum, HEADER_H, PANEL_W } from './ui';

export default function RatesApp(){
  const [tenor, setTenor] = useState("10Y");
  const spotNow = HISTORY[HISTORY.length-1][tenor];
  // === VIEW MODEL (unified across target / range / directional) ===
  const [viewMode, setViewMode] = useState('target');         // 'target' | 'range' | 'directional'
  const [target, setTarget] = useState(spotNow + 25);         // for 'target' mode
  const [inp, setInp] = useState((spotNow+25).toFixed(1));
  const [rangeLo, setRangeLo] = useState(spotNow - 25);       // for 'range' mode
  const [rangeHi, setRangeHi] = useState(spotNow + 25);
  const [inpLo, setInpLo] = useState((spotNow-25).toFixed(1));
  const [inpHi, setInpHi] = useState((spotNow+25).toFixed(1));
  const [direction, setDirection] = useState('up');           // for 'directional' mode
  const [confidence, setConfidence] = useState(0.5);          // P(in region) — default 50%
  const [exclusions, setExclusions] = useState([]);           // [{side:'above'|'below', level, confidence}]
  const [riskBudget, setRiskBudget] = useState(10);           // bp-of-notional
  const [activeExpiry,  setActiveExpiry]  = useState('6M');
  const [customExpDate, setCustomExpDate] = useState('');
  const [expDateLocked, setExpDateLocked] = useState(false);
  const [rank, setRank] = useState('user_e');                 // 'user_e' | 'edge' | 'bfb'
  const [idx, setIdx] = useState(0);
  const [showFwd, setShowFwd] = useState(true);
  const [showCones, setShowCones] = useState(true);
  const [showMAs, setShowMAs] = useState({ma50:false, ma100:false, ma200:false});
  const [historyWindow, setHistoryWindow] = useState('2Y');
  const [showDS, setShowDS] = useState(false);
  const [showDtcc, setShowDtcc] = useState(false);

  // Reset view levels when tenor changes
  useEffect(() => {
    const s = HISTORY[HISTORY.length-1][tenor];
    setTarget(s + 25); setInp((s+25).toFixed(1));
    setRangeLo(s - 25); setInpLo((s-25).toFixed(1));
    setRangeHi(s + 25); setInpHi((s+25).toFixed(1));
    setExclusions([]);
    setIdx(0);
  }, [tenor]);

  // Build the raw view once from state — consumed by runPipeline per fwdTenor.
  const rawView = useMemo(() => {
    const base = { confidence, exclusions };
    if (viewMode === 'target')      return { ...base, kind:'target', target };
    if (viewMode === 'range')       return { ...base, kind:'range', rangeLo, rangeHi };
    if (viewMode === 'directional') return { ...base, kind:'directional', direction };
    return base;
  }, [viewMode, target, rangeLo, rangeHi, direction, confidence, exclusions]);

  // Build ranked strategies
  // Ranked strategies via the 6-step pipeline (one run per active expiry).
  const results = useMemo(() => {
    const all = [];
    const warnSet = new Set();
    const anchor = HISTORY[HISTORY.length-1]?.date;
    if (customExpDate && anchor) {
      const T_req = Math.max(1/52, (new Date(customExpDate) - new Date(anchor)) / (365.25 * 86400000));
      const fwdTenor = `custom.${tenor}`;
      delete _cubeCache[fwdTenor];
      delete _splineCache[fwdTenor];
      const synth = buildCustomCube(tenor, T_req);
      if (synth) SURFACE[fwdTenor] = synth;
      if (SURFACE[fwdTenor]) {
        const rows = runPipeline(rawView, fwdTenor, riskBudget, rank);
        if (rows.warnings) rows.warnings.forEach(w => warnSet.add(w));
        all.push(...rows);
      }
    } else {
      const fwdTenor = `${activeExpiry}.${tenor}`;
      if (SURFACE[fwdTenor]) {
        const rows = runPipeline(rawView, fwdTenor, riskBudget, rank);
        if (rows.warnings) rows.warnings.forEach(w => warnSet.add(w));
        all.push(...rows);
      }
    }
    all.sort((a,b) => b.score - a.score);
    const top = all.slice(0, 40);
    top.warnings = Array.from(warnSet);
    return top;
  }, [tenor, activeExpiry, rawView, riskBudget, rank, customExpDate]);

  const sel = results[idx] || null;

  const goTarget = () => {
    const v = parseFloat(inp);
    if (!isNaN(v) && v > -500 && v < 500){ setTarget(v); setIdx(0); }
  };
  const goRange = () => {
    const lo = parseFloat(inpLo), hi = parseFloat(inpHi);
    if (!isNaN(lo) && !isNaN(hi)){
      setRangeLo(Math.min(lo,hi)); setRangeHi(Math.max(lo,hi));
      setIdx(0);
    }
  };
  const addExclusion = (side) => {
    const defaultLevel = side === 'above' ? spotNow + 75 : spotNow - 75;
    setExclusions(ex => ex.length >= 2 ? ex : [...ex, { side, level: defaultLevel, confidence: 0.12 }]);
    setIdx(0);
  };
  const removeExclusion = (i) => { setExclusions(ex => ex.filter((_,j) => j!==i)); setIdx(0); };
  const updateExclusion = (i, field, val) => {
    setExclusions(ex => ex.map((e,j) => j===i ? { ...e, [field]: val } : e));
    setIdx(0);
  };
  const onDrag = useCallback((r, clickDate) => {
    setTarget(r); setInp(r.toFixed(1)); setIdx(0);
    if (clickDate && !expDateLocked){
      setCustomExpDate(clickDate);
    }
  }, [expDateLocked]);
  const onDragRange = useCallback((which, lvl) => {
    // Chart drag (range mode) sets whichever bound was picked by the drag handler.
    if (which === 'lo'){ setRangeLo(lvl); setInpLo(lvl.toFixed(1)); }
    else if (which === 'hi'){ setRangeHi(lvl); setInpHi(lvl.toFixed(1)); }
    setIdx(0);
  }, []);

  // View badge text — depends on mode.
  const viewDir =
    viewMode === 'target'      ? (target > spotNow ? 'HIGHER' : 'LOWER') :
    viewMode === 'range'       ? 'RANGE' :
    viewMode === 'directional' ? (direction === 'up' ? 'HIGHER' : 'LOWER') : '';
  const viewColor =
    viewMode === 'target'      ? (target > spotNow ? C.rd : C.gn) :
    viewMode === 'range'       ? C.bl :
    viewMode === 'directional' ? (direction === 'up' ? C.rd : C.gn) : C.ac;
  const viewSummary =
    viewMode === 'target'      ? `${target.toFixed(1)}bp (${(target-spotNow>=0?'+':'')}${(target-spotNow).toFixed(1)})` :
    viewMode === 'range'       ? `[${Math.min(rangeLo,rangeHi).toFixed(1)}, ${Math.max(rangeLo,rangeHi).toFixed(1)}]bp` :
    viewMode === 'directional' ? (direction === 'up' ? `rates > ${spotNow.toFixed(1)}` : `rates < ${spotNow.toFixed(1)}`) : '';

  // Compute the date that matches the selected standard expiry, so the date picker
  // always reflects the active tenor even when no custom date has been typed.
  const anchor = HISTORY.length ? HISTORY[HISTORY.length-1].date : '';
  const expiryDateStr = anchor ? addDays(anchor, EXPIRY_DAYS[activeExpiry] || 0) : '';

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet"/>
      <style>{`* { box-sizing: border-box; } body { margin:0; } button:hover { filter: brightness(1.15); } .mono { font-family: 'IBM Plex Mono', monospace; }`}</style>

      {/* HEADER */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <div style={logoIcon(C.ac, C.rd)}>R</div>
          <div>
            <div style={S.pageTitle}>TANGOALPHA RATES</div>
            <div style={S.pageSub}>EUR Swaption Strategy Optimizer</div>
          </div>
        </div>
        <div style={S.headerRight}>
          <button onClick={()=>setShowDS(!showDS)} style={S.dataBtn}><span style={{width:5,height:5,borderRadius:'50%',background:C.gn}}/>DATA</button>
          <div style={S.asOf}>AS OF <span className="mono" style={{color:C.txD}}>{AS_OF}</span></div>
          <div style={{textAlign:'right'}}>
            <div style={S.spotLabel}>EUR {tenor}</div>
            <div className="mono" style={spotNum(C.ac)}>{spotNow.toFixed(2)}<span style={{fontSize:F.md, color:C.txD}}>bp</span></div>
          </div>
          <div style={badge(viewColor)}>
            {viewDir} → <span className="mono">{viewSummary}</span>
            {exclusions.length > 0 && <span style={{color:C.txD, marginLeft:6}}>· {exclusions.length} excl</span>}
          </div>
        </div>
      </div>
      {showDS && <div style={S.dsRow}>{DATA_SOURCES.map(ds=><div key={ds.id} style={S.dsChip}><span style={{width:5,height:5,borderRadius:'50%',background:ds.status==='ok'?C.gn:ds.status==='demo'?C.am:C.txM}}/><span style={{fontWeight:600,color:C.tx}}>{ds.name}</span><span style={{color:C.txD}}>{ds.desc}</span></div>)}</div>}

      {/* MAIN LAYOUT */}
      <div style={S.pageContent}>
        {/* LEFT COLUMN */}
        <div style={{borderRight:`1px solid ${C.bd}`, display:'flex', flexDirection:'column'}}>
          {/* Chart */}
          <div style={{padding:'8px 14px', borderBottom:`1px solid ${C.bd}`}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
              <div style={{display:'flex', gap:6, alignItems:'center'}}>
                <span style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em'}}>Underlying</span>
                {TENORS.map(t => (
                  <button key={t} onClick={()=>setTenor(t)} style={btn(t===tenor, C.ac, C.acD)}>{t}</button>
                ))}
                <div style={{width:1, height:16, background:C.bd, margin:'0 6px'}}/>
                <span style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em'}}>Range</span>
                {['6M','1Y','2Y','ALL'].map(w => (
                  <button key={w} onClick={()=>setHistoryWindow(w)} style={btn(w===historyWindow, C.bl, C.blD)}>{w}</button>
                ))}
              </div>
              <div style={{display:'flex', gap:3}}>
                <button onClick={()=>setShowCones(!showCones)} style={btn(showCones, C.rd, 'rgba(248,113,113,.1)')}>Δ CONES</button>
                <button onClick={()=>setShowFwd(!showFwd)}   style={btn(showFwd, C.am, C.amD)}>FWDS</button>
                <button onClick={()=>setShowDtcc(!showDtcc)} style={btn(showDtcc, C.gn, 'rgba(63,185,80,.12)')}>DTCC</button>
                {['ma50','ma100','ma200'].map(k => (
                  <button key={k} onClick={()=>setShowMAs(p=>({...p,[k]:!p[k]}))}
                    style={btn(showMAs[k], C.txD, 'rgba(148,163,184,.1)')}>
                    {k.replace('ma','').toUpperCase()}d
                  </button>
                ))}
              </div>
            </div>
            <RateChart tenor={tenor}
                       target={viewMode==='target' ? target : null}
                       viewMode={viewMode}
                       rangeLo={viewMode==='range' ? rangeLo : null}
                       rangeHi={viewMode==='range' ? rangeHi : null}
                       onDrag={onDrag}
                       onDragRange={onDragRange}
                       showFwd={showFwd} showCones={showCones} showMAs={showMAs}
                       historyWindow={historyWindow}
                       expiryDate={sel ? (sel.expiry === 'custom' ? customExpDate : addDays(HISTORY[HISTORY.length-1].date, EXPIRY_DAYS[sel.expiry])) : null}/>
          </div>

          {/* VIEW MODE + EXPIRY + RISK BUDGET */}
          <div style={{padding:'8px 14px', borderBottom:`1px solid ${C.bd}`,
                       display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
            <span style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>View</span>
            {[['target','TARGET'],['range','RANGE'],['directional','DIRECTIONAL']].map(([m,l])=>(
              <button key={m} onClick={()=>{setViewMode(m); setIdx(0);}} style={btn(viewMode===m, C.ac, C.acD)}>{l}</button>
            ))}
            <div style={{width:1, height:20, background:C.bd, margin:'0 4px'}}/>
            {viewMode === 'target' && <>
              <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Target</label>
              <input type="text" value={inp} onChange={e=>setInp(e.target.value)}
                     onKeyDown={e=>e.key==='Enter' && goTarget()}
                     onBlur={goTarget}
                     style={{background:C.bg, border:`1px solid ${C.bd}`, borderRadius:3,
                             padding:'4px 7px', color:C.tx, fontSize:F.xl, width:64,
                             fontFamily:'IBM Plex Mono, monospace', outline:'none'}}/>
              <span style={{fontSize:F.xs, color:C.txD}}>bp</span>
            </>}
            {viewMode === 'range' && <>
              <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Lo</label>
              <input type="text" value={inpLo} onChange={e=>setInpLo(e.target.value)}
                     onKeyDown={e=>e.key==='Enter' && goRange()}
                     onBlur={goRange}
                     style={{background:C.bg, border:`1px solid ${C.bd}`, borderRadius:3,
                             padding:'4px 7px', color:C.tx, fontSize:F.xl, width:58,
                             fontFamily:'IBM Plex Mono, monospace', outline:'none'}}/>
              <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Hi</label>
              <input type="text" value={inpHi} onChange={e=>setInpHi(e.target.value)}
                     onKeyDown={e=>e.key==='Enter' && goRange()}
                     onBlur={goRange}
                     style={{background:C.bg, border:`1px solid ${C.bd}`, borderRadius:3,
                             padding:'4px 7px', color:C.tx, fontSize:F.xl, width:58,
                             fontFamily:'IBM Plex Mono, monospace', outline:'none'}}/>
              <span style={{fontSize:F.xs, color:C.txD}}>bp</span>
            </>}
            {viewMode === 'directional' && <>
              <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Direction</label>
              {[['up','↑ HIGHER', C.rd],['down','↓ LOWER', C.gn]].map(([d,l,col])=>(
                <button key={d} onClick={()=>{setDirection(d); setIdx(0);}} style={btn(direction===d, col)}>{l}</button>
              ))}
              <span style={{fontSize:F.xs, color:C.txM, marginLeft:4}}>
                (anchor = spot {spotNow.toFixed(1)}bp; add an exclusion to unlock risk reversals)
              </span>
            </>}
            <div style={{width:1, height:20, background:C.bd, margin:'0 4px'}}/>
            <span style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Exp</span>
            {EXPIRIES.map(e => (
              <button key={e} onClick={()=>{setActiveExpiry(e); setCustomExpDate(''); setIdx(0);}}
                style={{ ...btn(!customExpDate && activeExpiry===e, C.ac, C.acD), opacity: customExpDate ? 0.4 : 1 }}>
                {e}
              </button>
            ))}
            <div style={{width:1, height:14, background:C.bd, margin:'0 2px'}}/>
            <input type="date" value={customExpDate || expiryDateStr}
              min={anchor ? addDays(anchor, 1) : ''}
              max={anchor ? addDays(anchor, 400) : ''}
              onChange={e => setCustomExpDate(e.target.value)}
              style={{fontSize:F.sm, background:customExpDate ? C.acD : C.bg, color:customExpDate ? C.ac : C.txD,
                      border:`1px solid ${customExpDate ? C.ac : C.bd}`, borderRadius:2, padding:'2px 4px',
                      fontFamily:'IBM Plex Mono, monospace', colorScheme:'dark', cursor:'pointer'}}/>
            {customExpDate && <button onClick={()=>setCustomExpDate('')}
              style={{background:'transparent', border:'none', color:C.txD, cursor:'pointer',
                      fontSize:F.lg, padding:'0 2px'}}>✕</button>}
            <button onClick={()=>setExpDateLocked(l=>!l)}
              title={expDateLocked ? 'Unlock: chart clicks will update the expiry date' : 'Lock: chart clicks won\'t change the expiry date'}
              style={{background:expDateLocked ? C.acD : 'transparent', border:`1px solid ${expDateLocked ? C.ac : C.bd}`,
                      borderRadius:2, padding:'2px 5px', color:expDateLocked ? C.ac : C.txD,
                      cursor:'pointer', display:'flex', alignItems:'center'}}>
              {expDateLocked
                ? <svg width="12" height="14" viewBox="0 0 11 13" fill="none"><rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3.5 5.5V3.5a2 2 0 0 1 4 0V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                : <svg width="12" height="14" viewBox="0 0 11 13" fill="none"><rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3.5 5.5V3.5a2 2 0 0 1 4 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              }
            </button>
            <div style={{width:1, height:20, background:C.bd, margin:'0 4px'}}/>
            <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>R</label>
            <input type="number" value={riskBudget} min="1" max="200"
                   onChange={e=>{const v = parseFloat(e.target.value); if(!isNaN(v)&&v>0) {setRiskBudget(v); setIdx(0);}}}
                   style={{background:C.bg, border:`1px solid ${C.bd}`, borderRadius:3,
                           padding:'3px 5px', color:C.tx, fontSize:F.md, width:48,
                           fontFamily:'IBM Plex Mono, monospace', outline:'none'}}/>
            <span style={{fontSize:F.xs, color:C.txD}}>bp</span>
            <div style={{flex:1}}/>
            <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Conf</label>
            <input type="range" min="0.2" max="0.9" step="0.05" value={confidence}
                   onChange={e=>{setConfidence(parseFloat(e.target.value)); setIdx(0);}}
                   style={{width:70, accentColor:C.ac}}/>
            <span className="mono" style={{fontSize:F.md, color:C.ac, fontWeight:700, width:30}}>
              {(confidence*100).toFixed(0)}%
            </span>
          </div>

          {/* EXCLUSIONS panel */}
          <div style={{padding:'6px 14px', borderBottom:`1px solid ${C.bd}`,
                       display:'flex', gap:10, alignItems:'center', flexWrap:'wrap'}}>
            <span style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Exclusions</span>
            <span style={{fontSize:F.xxs, color:C.txM, fontStyle:'italic'}}>
              levels you don't think we'll reach — unlock risk reversals / capped ratios
            </span>
            {exclusions.map((e, i) => (
              <ExclusionRow key={i} idx={i} excl={e}
                            onUpdate={updateExclusion} onRemove={removeExclusion}/>
            ))}
            {exclusions.length < 2 && <>
              <button onClick={()=>addExclusion('above')} style={{ ...S.btnGhost, border:`1px dashed ${C.bd}`, color:C.rd }}>+ ↑ above</button>
              <button onClick={()=>addExclusion('below')} style={{ ...S.btnGhost, border:`1px dashed ${C.bd}`, color:C.gn }}>+ ↓ below</button>
            </>}
          </div>

          {/* Ranking */}
          <div style={{padding:'6px 14px', borderBottom:`1px solid ${C.bd}`,
                       display:'flex', gap:5, alignItems:'center', flexWrap:'wrap'}}>
            <span style={{fontSize:F.xs, color:C.txM, textTransform:'uppercase', letterSpacing:'.08em'}}>Rank</span>
            {[['user_e','E[user]'],['edge','Edge (user−mkt)'],['bfb','Bang/Buck']].map(([m,l])=>(
              <button key={m} onClick={()=>{setRank(m); setIdx(0);}} style={btn(rank===m, C.ac, C.acD)}>{l}</button>
            ))}
            <div style={{flex:1}}/>
          </div>

          {/* View warnings (e.g. target adjusted to grid resolution) */}
          {results.warnings && results.warnings.length > 0 && (
            <div style={{padding:'6px 14px', borderBottom:`1px solid ${C.bd}`,
                          background:'rgba(245,158,11,.06)', borderLeft:`3px solid ${C.am}`}}>
              {results.warnings.map((w, i) => (
                <div key={i} style={{fontSize:F.sm, color:C.am, marginBottom: i < results.warnings.length-1 ? 2 : 0}}>
                  <span style={{fontWeight:700, marginRight:4}}>⚠</span>{w}
                </div>
              ))}
            </div>
          )}

          {/* "At R" label strip */}
          <div style={{padding:'4px 14px', borderBottom:`1px solid ${C.bd}`,
                        display:'flex', justifyContent:'space-between', alignItems:'center',
                        fontSize:F.xs, color:C.txM, background:C.bg}}>
            <span style={{textTransform:'uppercase', letterSpacing:'.08em'}}>
              Amounts scaled to R = <span className="mono" style={{color:C.ac, fontWeight:700}}>{riskBudget}bp</span>
            </span>
            <span style={{fontStyle:'italic'}}>size × per-unit shown in detail panel</span>
          </div>

          {/* Strategy table */}
          <div style={{flex:1, overflow:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:F.lg}}>
              <thead style={{background:C.bgC, position:'sticky', top:0, zIndex:1}}>
                <tr style={{borderBottom:`1px solid ${C.bd}`}}>
                  <th style={{...S.th, textAlign:'left',  padding:'8px 12px'}}>Structure</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 6px'}}>Exp</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 6px'}}>Prem</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 6px'}}>E[user]</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 6px'}}>E[mkt]</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 6px'}}>Edge</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 6px'}}>P gap</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 6px'}}>Size</th>
                  <th style={{...S.th, textAlign:'right', padding:'8px 12px'}}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {results.length === 0 && <tr><td colSpan={9} style={{padding:20, textAlign:'center', color:C.txM, fontSize:F.md}}>Set a view to see candidate structures.</td></tr>}
                {results.map((r, i) => (
                  <tr key={i} onClick={()=>setIdx(i)}
                      style={{borderBottom:`1px solid ${C.bd}`, cursor:'pointer',
                              background: i===idx ? C.bgH : 'transparent'}}>
                    <td style={{padding:'7px 12px', color:i===idx?C.ac:C.tx, fontWeight:i===idx?600:500, fontSize:F.md}}>
                      {r.archetype}
                      {r.isUncapped && <span style={{color:C.am, marginLeft:6, fontSize:F.xs, fontWeight:700}}>UNCAPPED</span>}
                      {r.offGrid && <span style={{color:C.rd, marginLeft:4, fontSize:F.xs}}>*</span>}
                    </td>
                    <td style={{textAlign:'right', padding:'7px 6px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md, color:C.txD}}>{r.expiry}</td>
                    <td style={{textAlign:'right', padding:'7px 6px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md,
                                color: r.netDebit > 0 ? C.rd : C.gn}}>
                      {r.netDebit > 0 ? '' : '+'}{(-r.netDebit).toFixed(1)}
                    </td>
                    <td style={{textAlign:'right', padding:'7px 6px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md,
                                color: r.eUser >= 0 ? C.gn : C.rd, fontWeight:600}}>
                      {r.eUser>=0?'+':''}{r.eUser.toFixed(1)}
                    </td>
                    <td style={{textAlign:'right', padding:'7px 6px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md,
                                color: C.txD}}>
                      {r.eMarket>=0?'+':''}{r.eMarket.toFixed(1)}
                    </td>
                    <td style={{textAlign:'right', padding:'7px 6px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md,
                                color: r.eEdge >= 0 ? C.ac : C.txD, fontWeight: r.eEdge >= 2 ? 700 : 500}}>
                      {r.eEdge>=0?'+':''}{r.eEdge.toFixed(1)}
                    </td>
                    <td style={{textAlign:'right', padding:'7px 6px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md, color:C.pu}}>
                      {r.pGap>=0?'+':''}{(r.pGap*100).toFixed(0)}pp
                    </td>
                    <td style={{textAlign:'right', padding:'7px 6px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md, color:C.bl}}>
                      {r.size.toFixed(2)}×
                    </td>
                    <td style={{textAlign:'right', padding:'7px 12px', fontFamily:'IBM Plex Mono, monospace', fontSize:F.md,
                                color: r.isUncapped ? C.am : C.rd}}>
                      {r.isUncapped ? r.tail3sig.toFixed(0) : r.maxLoss.toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT COLUMN - detail panel */}
        <div style={{display:'flex', flexDirection:'column', background:C.bgC, overflow:'auto', maxHeight:'calc(100vh - 52px)'}}>
          {sel ? (
            <>
              <div style={{padding:'12px 16px', borderBottom:`1px solid ${C.bd}`}}>
                <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:4}}>Selected · #{idx+1}</div>
                <div style={{fontSize:F.h3, fontWeight:600, color:C.ac, marginBottom:2}}>{sel.archetype}</div>
                <div style={{fontSize:F.sm, color:C.txM, fontStyle:'italic', marginBottom:3}}>{sel.rationale}</div>
                <div style={{fontSize:F.md, color:C.txD}}>
                  <span className="mono">{sel.expiry}</span> into <span className="mono">{tenor}</span>
                  {' · '}ATM Fwd <span className="mono">{sel.atmBp.toFixed(1)}bp</span>
                  {' · '}mkt mode <span className="mono" style={{color:C.pu}}>{sel.mode.toFixed(1)}bp</span>
                </div>
              </div>

              {/* Legs */}
              <div style={{padding:'10px 16px', borderBottom:`1px solid ${C.bd}`}}>
                <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:6}}>Legs</div>
                {sel.strategy.legs.map((l, i) => (
                  <div key={i} style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                                         padding:'5px 0', fontSize:F.md, borderTop: i>0?`1px dashed ${C.bd}`:'none'}}>
                    <div>
                      <span style={{color: l.side>0 ? C.gn : C.rd, fontWeight:700, marginRight:6,
                                     fontFamily:'IBM Plex Mono, monospace'}}>
                        {l.side>0?'+':'-'}{l.ratio!==1?l.ratio+'×':''}
                      </span>
                      <span style={{color: l.isPayer?C.bl:C.gn, fontWeight:600}}>
                        {l.isPayer?'Payer':'Receiver'}
                      </span>
                      <span style={{color:C.txD, marginLeft:4}} className="mono">
                        K={l.K_bp.toFixed(1)}bp
                      </span>
                    </div>
                    <div style={{textAlign:'right', color:C.txD, fontSize:F.sm}} className="mono">
                      σ={l.priced.vol.toFixed(1)}bp · prem={l.priced.premium_bp.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* USER vs MARKET panel (the key decision surface) */}
              <div style={{padding:'10px 16px', borderBottom:`1px solid ${C.bd}`}}>
                <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:6,
                              display:'flex', justifyContent:'space-between'}}>
                  <span>User vs Market</span>
                  <span style={{color:C.txM, fontWeight:400, textTransform:'none', letterSpacing:0, fontStyle:'italic'}}>
                    at R = {riskBudget}bp
                  </span>
                </div>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'4px 10px', fontSize:F.md}}>
                  <div/>
                  <div style={{fontSize:F.xs, color:C.ac, textTransform:'uppercase', textAlign:'right'}}>User</div>
                  <div style={{fontSize:F.xs, color:C.pu, textTransform:'uppercase', textAlign:'right'}}>Market</div>
                  <span style={{color:C.txD, fontSize:F.sm, textTransform:'uppercase'}}>E[P&L]</span>
                  <span className="mono" style={{color:sel.eUser>=0?C.gn:C.rd, fontWeight:600, textAlign:'right'}}>
                    {sel.eUser>=0?'+':''}{sel.eUser.toFixed(2)}bp
                  </span>
                  <span className="mono" style={{color:sel.eMarket>=0?C.gn:C.rd, fontWeight:500, textAlign:'right'}}>
                    {sel.eMarket>=0?'+':''}{sel.eMarket.toFixed(2)}bp
                  </span>
                  <span style={{color:C.txM, fontSize:F.xs, fontStyle:'italic'}}>per unit</span>
                  <span className="mono" style={{color:C.txD, fontWeight:400, textAlign:'right', fontSize:F.sm}}>
                    {sel.perUnit.eUser>=0?'+':''}{sel.perUnit.eUser.toFixed(2)}
                  </span>
                  <span className="mono" style={{color:C.txD, fontWeight:400, textAlign:'right', fontSize:F.sm}}>
                    {sel.perUnit.eMarket>=0?'+':''}{sel.perUnit.eMarket.toFixed(2)}
                  </span>
                  <span style={{color:C.txD, fontSize:F.sm, textTransform:'uppercase'}}>P(region)</span>
                  <span className="mono" style={{color:C.ac, fontWeight:600, textAlign:'right'}}>
                    {(sel.pUserRegion*100).toFixed(1)}%
                  </span>
                  <span className="mono" style={{color:C.pu, fontWeight:500, textAlign:'right'}}>
                    {(sel.pMktRegion*100).toFixed(1)}%
                  </span>
                </div>
                <div style={{marginTop:6, paddingTop:6, borderTop:`1px dashed ${C.bd}`,
                              display:'flex', justifyContent:'space-between', fontSize:F.md}}>
                  <span style={{color:C.txD, fontSize:F.sm, textTransform:'uppercase'}}>Edge (user − mkt)</span>
                  <span className="mono" style={{color:sel.eEdge>=0?C.ac:C.rd, fontWeight:700}}>
                    {sel.eEdge>=0?'+':''}{sel.eEdge.toFixed(2)}bp
                  </span>
                </div>
                <div style={{display:'flex', justifyContent:'space-between', fontSize:F.md}}>
                  <span style={{color:C.txD, fontSize:F.sm, textTransform:'uppercase'}}>P gap</span>
                  <span className="mono" style={{color:C.pu, fontWeight:600}}>
                    {sel.pGap>=0?'+':''}{(sel.pGap*100).toFixed(1)}pp
                  </span>
                </div>
              </div>

              {/* Risk / sizing */}
              <div style={{padding:'10px 16px', borderBottom:`1px solid ${C.bd}`, display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px', fontSize:F.md}}>
                {[
                  ['Net Debit', `${sel.netDebit>0?'':'+'}${(-sel.netDebit).toFixed(2)}bp`, sel.netDebit>0?C.rd:C.gn],
                  ['Size (at R)', `${sel.size.toFixed(2)}×`, C.bl],
                  ['Max Gain', sel.maxGain > 500 ? 'Uncapped' : `${sel.maxGain.toFixed(2)}bp`, C.gn],
                  [sel.isUncapped ? 'Max Loss (tail-σ)' : 'Max Loss',
                    sel.isUncapped ? `3σ ${sel.tail3sig.toFixed(1)}bp` : `${sel.maxLoss.toFixed(2)}bp`,
                    sel.isUncapped ? C.am : C.rd],
                  ['2σ adverse', `${sel.tail2sig.toFixed(1)}bp`, C.rd],
                  ['3σ adverse', `${sel.tail3sig.toFixed(1)}bp`, C.rd],
                  ['Net Delta', `${sel.stats.delta>=0?'+':''}${sel.stats.delta.toFixed(3)}`, C.cy],
                  ['Net Vega', `${sel.stats.vega>=0?'+':''}${sel.stats.vega.toFixed(3)}`, C.cy],
                ].map(([k,v,col]) => (
                  <div key={k} style={{display:'flex', justifyContent:'space-between'}}>
                    <span style={{color:C.txD, fontSize:F.sm, textTransform:'uppercase', letterSpacing:'.05em'}}>{k}</span>
                    <span className="mono" style={{color:col, fontWeight:600}}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Moontower check */}
              <div style={{padding:'8px 16px', borderBottom:`1px solid ${C.bd}`, fontSize:F.sm,
                            background: sel.moontower.ok ? 'rgba(52,211,153,.04)' : 'rgba(245,158,11,.06)',
                            borderLeft:`3px solid ${sel.moontower.ok ? C.gn : C.am}`}}>
                <div style={{color: sel.moontower.ok ? C.gn : C.am, fontWeight:700, fontSize:F.xs,
                              textTransform:'uppercase', letterSpacing:'.08em', marginBottom:2}}>
                  {sel.moontower.ok ? '✓' : '⚠'} Moontower check
                </div>
                <div style={{color:C.txD}}>{sel.moontower.note}</div>
                {!sel.moontower.ok && (
                  <div style={{color:C.txM, fontStyle:'italic', marginTop:2}}>
                    Short leg at your target (not market mode) prices specificity the market disagrees with — may over-cheapen for the wrong reason.
                  </div>
                )}
              </div>

              {/* Breakevens */}
              {sel.stats.breakevens.length > 0 && (
                <div style={{padding:'8px 16px', borderBottom:`1px solid ${C.bd}`, fontSize:F.md}}>
                  <span style={{color:C.txD, fontSize:F.sm, textTransform:'uppercase', letterSpacing:'.05em', marginRight:8}}>Breakeven</span>
                  {sel.stats.breakevens.map((b,i) => (
                    <span key={i} className="mono" style={{color:C.ac, fontWeight:600, marginRight:10}}>
                      {b.toFixed(1)}bp
                    </span>
                  ))}
                </div>
              )}

              {/* Payoff chart */}
              <div style={{padding:'10px 12px', borderBottom:`1px solid ${C.bd}`}}>
                <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:4}}>
                  Payoff at Expiry · <span style={{color:C.pu}}>RN Density</span>
                </div>
                <PayoffChart profile={sel.stats.profile} atmBp={sel.atmBp}
                              target={viewMode==='target' ? target : null}
                              density={readCube(sel.fwdTenor)?.density}/>
              </div>

              {/* Vol smile */}
              <div style={{padding:'10px 16px', borderBottom:`1px solid ${C.bd}`}}>
                <SmilePanel fwdTenor={sel.fwdTenor}/>
              </div>
            </>
          ) : (
            <div style={{padding:40, textAlign:'center', color:C.txM, fontSize:F.lg}}>
              Select a strategy from the list.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TANGOALPHACURVE - Curve trades (steepeners / flatteners)
// DV01-weighted swaption pairs across two tenors.
// ============================================================

// Each strategy: which leg type on the short tenor, which on the long tenor.
// side +1 = buy, -1 = sell. All sized so that DV01_S × N_S = DV01_L × N_L.
// spreadSign: the sign of the linear (swap-pair) trade's P&L per +1bp of spread move.
//   +1 = bullish on spread (steepeners), -1 = bearish on spread (flatteners).
